import { gql } from '@reverbdotcom/commons/src/gql';
import React, { useCallback } from 'react';
import I18n from 'i18n-js';
import { withRouter, WithRouterProps } from 'react-router';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useHOCMutation, MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { useHelpCenterArticleUrl } from '@reverbdotcom/commons/src/url_helpers';
import { ErrorList } from '@reverbdotcom/commons/src/ErrorList';
import { RCTextInput, RCCheckbox, RCAlertBox } from '@reverbdotcom/cadence/components';
import { connect } from '@reverbdotcom/commons/src/connect';

import { useCreditCardForm } from './credit_card_form_context';
import { CreditCardSummary } from './CreditCardSummary';
import AddressFormFields from './address_form_fields';

import {
  CoreCreditCardWalletUpdateMyCreditCard,
  Input_core_apimessages_UpdateMyCreditCardRequest as UpdateMyCreditCardRequest,
  core_apimessages_AddressEntry as AddressEntry,
  core_apimessages_Country as Country,
  Core_CreditCardWallet_UpdateMyCreditCardMutation,
  Core_CreditCardWallet_UpdateMyCreditCardMutationVariables,
  CreditCardEditFormFieldsFragment,
} from '@reverbdotcom/commons/src/gql/graphql';
import CreditCardEncryptedDateInput from './credit_card_encrypted_date_input';
import CreditCardEncryptedInput from './credit_card_encrypted_input';
import { AdyenCardType } from '@reverbdotcom/commons/src/adyen';
import { useAdyenCard } from '@reverbdotcom/commons/src/adyen_card_hook';

type CreditCard = CreditCardEditFormFieldsFragment['creditCards'][0];

type UpdateMyCreditCardMutationFn = MutationFunction<
  Core_CreditCardWallet_UpdateMyCreditCardMutation,
  Core_CreditCardWallet_UpdateMyCreditCardMutationVariables
>;

interface MutationProps {
  updateMyCreditCard: UpdateMyCreditCardMutationFn;
}

interface ExternalProps {
  countries: Country[];
  creditCard: CreditCard;
}

type Props = ExternalProps & MutationProps & WithRouterProps;

const CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID = '1500005385482';
const COMPONENT_NAME = 'CreditCardEditForm';

export function CreditCardEditForm(props: Props) {
  const form = useCreditCardForm();
  const selectedCard = props.creditCard;
  const cardAddress = selectedCard.address;

  const [saveAsDefault, setSaveAsDefault] = React.useState(selectedCard.primaryForCheckout);

  const { cardState: encryptedCardState, focus, errors: adyenErrors } = useAdyenCard({
    selector: '[data-card-fields]',
    cardType: AdyenCardType.Card,
  });

  const { paymentMethod: encryptedPaymentMethod } = encryptedCardState.data;

  const [creditCard, setCreditCard] = React.useState({
    cardholderName: selectedCard.cardholderName,
    primaryForCheckout: saveAsDefault,
  });

  const [billingAddress, setBillingAddress] = React.useState<AddressEntry>({
    countryCode: cardAddress?.countryCode,
    name: cardAddress?.name,
    streetAddress: cardAddress?.streetAddress,
    extendedAddress: cardAddress?.extendedAddress,
    locality: cardAddress?.locality,
    region: cardAddress?.region,
    postalCode: cardAddress?.postalCode,
    phone: cardAddress?.phone,
  });

  const { updateMyCreditCard, errors } = useUpdateMyCreditCard(
    props.updateMyCreditCard,
  );

  async function onSubmit(e) {
    e.preventDefault();
    form.setLoading(true);

    const input = {
      id: selectedCard.id,
      creditCard: {
        ...creditCard,
        primaryForCheckout: saveAsDefault,
        tokenizedFields: {
          encryptedExpiryMonth: encryptedPaymentMethod.encryptedExpiryMonth,
          encryptedExpiryYear: encryptedPaymentMethod.encryptedExpiryYear,
          encryptedSecurityCode: encryptedPaymentMethod.encryptedSecurityCode,
        },
      },
      billingAddress,
    };

    const result = await updateMyCreditCard(input);

    if (result.data) {
      form.onSelect(selectedCard.id);
    } else {
      form.setLoading(false);
    }
  }

  function onChange(e) {
    setCreditCard({
      ...creditCard,
      [e.target.name]: e.target.value,
    });
  }

  const troubleshootingUrl = useHelpCenterArticleUrl(CREDIT_CARD_TROUBLESHOOTING_TIPS_ARTICLE_ID);

  return (
    <form id={form.id} onSubmit={onSubmit}>
      {errors.length > 0 && (
        <div className="mb-4">
          <RCAlertBox type="error">
            <ErrorList errors={errors} />
            <div className="mt-2">
              <a href={troubleshootingUrl}>
                {I18n.t('discovery.creditCardWallet.cardTroubleshooting')}
              </a>
            </div>
          </RCAlertBox>
        </div>
      )}
      <CreditCardSummary creditCard={props.creditCard} />
      <div className="g-container d-flex fx-dir-col mtb-4">
        <div data-card-fields className="d-flex gap-2 mb-2">
          <div className="g-col-4 g-col-tablet-12 tablet-mb-2">
            <CreditCardEncryptedDateInput
              paymentMethod={encryptedPaymentMethod}
              label={I18n.t('discovery.creditCardForm.expiration')}
              errors={adyenErrors}
              focus={focus}
            />
          </div>

          <div className="g-col-4 g-col-mobile-12 scaling-mb-4">
            <CreditCardEncryptedInput
              inputValue={encryptedPaymentMethod.encryptedSecurityCode}
              label={I18n.t('discovery.creditCardForm.cvv')}
              cseName="encryptedSecurityCode"
              errors={adyenErrors}
              focus={focus}
            />
          </div>
        </div>
        <RCTextInput
          label={I18n.t('discovery.creditCardWallet.editForm.cardholderName')}
          name="cardholderName"
          value={creditCard.cardholderName}
          id="cardholderName"
          type="text"
          placeholder={I18n.t('discovery.creditCardWallet.editForm.cardholderName')}
          onChange={onChange}
        />
      </div>
      <RCCheckbox
        id="credit-card-edit-billing"
        name="credit-card-edit-billing"
        checked={saveAsDefault}
        onChange={() => setSaveAsDefault(prev => !prev)}
        label={I18n.t('discovery.creditCardWallet.editForm.saveDefault')}
      />
      <>
        <div className="bdt-1 bd-color-primary mtb-4" />
        <AddressFormFields
          address={billingAddress}
          countries={props.countries}
          updateField={setBillingAddress}
          useCamelizedFieldsForRQL
          trackingComponentName={COMPONENT_NAME}
        />
      </>
    </form>
  );
}

export const CreditCardEditFormFragment = gql(`
  fragment CreditCardEditFormFields on rql_Me {
    _id
    creditCards {
      id
      brand {
        type
        name
      }
      display {
        summary
        expirationDate
      }
      address {
        _id
        countryCode
        name
        streetAddress
        extendedAddress
        locality
        region
        postalCode
        phone
      }
      expirationMonth
      expirationYear
      cardholderName
      primaryForCheckout
    }
  }
`);

export function useUpdateMyCreditCard(
  updateMyCreditCard: UpdateMyCreditCardMutationFn,
) {
  const [mutate, { loading, errors }] = useHOCMutation(updateMyCreditCard);

  const updateMyCreditCardCallback = useCallback(async (input: UpdateMyCreditCardRequest) => {
    return mutate({
      variables: {
        input: {
          ...input,
        },
      },
    });
  }, [mutate]);

  return { updateMyCreditCard: updateMyCreditCardCallback, loading, errors };
}

export const UPDATE_MY_CREDIT_CARD_MUTATION = gql(`
  mutation Core_CreditCardWallet_UpdateMyCreditCard(
      $input: Input_core_apimessages_UpdateMyCreditCardRequest
    ) {
      updateMyCreditCard(input: $input) {
        creditCard {
          id
          cardholderName
          primaryForCheckout
          display {
            summary
            expirationDate
          }
          address {
            _id
            name
            streetAddress
            extendedAddress
            locality
            region
            regionName
            postalCode
            phone
            countryCode
            country {
              _id
              countryCode
            }
          }
        }
      }
    }
`);

const withMutation = withGraphql<Props, CoreCreditCardWalletUpdateMyCreditCard.Mutation, CoreCreditCardWalletUpdateMyCreditCard.Variables>(
  UPDATE_MY_CREDIT_CARD_MUTATION,
  {
    name: 'updateMyCreditCard',
  },
);

export default connect<ExternalProps>([
  withRouter,
  withMutation,
])(CreditCardEditForm);
